import React, { useEffect, useState } from 'react'
import moment from 'moment'
import ComingSoon from '../../../../components/ComingSoon'
import { Alert, Avatar, Chip, Stack, Tooltip, Typography } from '@mui/material'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff'
import API from '../../../../services/API'
import { useNavigate } from 'react-router-dom'

const Audits = () => {
  const [audits, setAudits] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    API.get('/medsmx/audits')
      .then((res) => {
        if (res.status === 200) {
          setAudits(res.data)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  return (
    <Stack gap={1}>
      <Typography variant="title2">Medicine Audits</Typography>
      {fetchError && (
        <Alert severity="error">
          Unable to load medicine audits. Please try again later.
        </Alert>
      )}
      <CustomDataGrid
        norowsoverlay={
          <Alert severity="info">
            No medicine audits recorded yet. Audits are completed against a
            medicine bag — open a bag from Medicines Management (or scan its QR
            code) and choose Record Audit. Completed audits then appear here.
          </Alert>
        }
        rows={audits}
        getRowId={(params) => params._id}
        disableRowSelectionOnClick
        onRowClick={({ row }) =>
          navigate(`/medicines-management/audits/${row._id}`)
        }
        loading={loading}
        columns={[
          {
            field: 'createdAt',
            renderCell: ({ row }) =>
              moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
            valueGetter: (value) => moment(value).toISOString(),
            headerName: 'Date',
            minWidth: 200,
            flex: 0.5,
          },
          {
            field: 'completedBy',
            display: 'flex',
            headerName: 'Completed By',
            minWidth: 250,
            renderCell: ({ row }) => (
              <Stack direction="row" gap={1} alignItems="center">
                <Avatar
                  src={row.completedBy?.photograph}
                  sx={{ width: 20, height: 20 }}
                />
                <Typography>
                  {row.completedBy?.firstName} {row.completedBy?.lastName}
                </Typography>
              </Stack>
            ),
            flex: 1,
          },
          {
            field: 'location',
            renderCell: ({ row }) => row.location?.name,
            headerName: 'Location',
            minWidth: 200,
          },
          {
            field: 'tagNumberMatch',
            headerName: 'Tag Match',
            minWidth: 150,
            display: 'flex',
            renderCell: ({ row }) =>
              row.bagOpened ? (
                <Tooltip title="Bag Opened">
                  <LockOpenIcon color="anMainBlue" />
                </Tooltip>
              ) : row.tagNumberMatch ? (
                <CheckCircleIcon color="success" />
              ) : (
                <CancelIcon color="error" />
              ),
            flex: 0.4,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'discrepancies',
            headerName: 'Discrepancies',
            headerAlign: 'center',
            align: 'center',
            minWidth: 100,
            display: 'flex',
            renderCell: ({ row }) =>
              row.discrepancies ? (
                <Stack alignItems="center" gap={1} direction="row">
                  <CheckCircleIcon color="error" />
                  {!row.reviewedBy && (
                    <Tooltip title="Not Reviewed">
                      <SpeakerNotesOffIcon color="warning" />
                    </Tooltip>
                  )}
                </Stack>
              ) : (
                <CancelIcon color="success" />
              ),
            flex: 0.4,
          },
        ]}
      />
    </Stack>
  )
}

export default Audits
